"use client";

import React, { useCallback, useState, useEffect } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { Slider } from "@lmn/design-system";
import styled, { DefaultTheme } from "styled-components";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";

const SliderWrapper = styled.div<{
  theme: DefaultTheme;
}>(() => ({
  marginTop: "1rem",
  "& > div": {
    backgroundColor: "transparent",
  },
}));

interface PriceFilterProps {
  delayedUpdate?: boolean;
}

const PriceFilter: React.FC<PriceFilterProps> = ({ delayedUpdate = false }) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();

  const [value, setValue] = useState(() => {
    return delayedUpdate
      ? Number(tempFilters.get("price"))
      : Number(searchParams.get("price")) || 6000;
  });

  useEffect(() => {
    setValue(Number(searchParams.get("price")) || 6000);
  }, [searchParams]);

  const handleChange = useCallback(
    (newValue: number | number[]) => {
      const singleValue = Array.isArray(newValue) ? newValue[1] : newValue;
      setValue(singleValue);

      const filterValue = singleValue === 6000 ? null : singleValue.toString();

      if (delayedUpdate) {
        // Just update temp filters, will be committed on dialog confirmation
        updateTempFilter("price", filterValue);
      } else {
        // Immediate URL update
        updateFilter("price", filterValue);
      }
    },
    [updateFilter, updateTempFilter, delayedUpdate]
  );

  const formatter = (val: number) => {
    if (val === 6000) return "Beliebig";
    return `${val} €`;
  };

  return (
    <SliderWrapper data-testid="priceFilter">
      <Slider
        rangeMin={100}
        rangeMax={6000}
        step={100}
        selectedValue={value}
        formatter={formatter}
        handleCommittedChange={handleChange}
      />
    </SliderWrapper>
  );
};

export default PriceFilter;
